import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  AnswerComposer,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  ErrorText,
  HelpTip,
  LinkedDocumentList,
  PersonDisplay,
  PublishedQuestionList,
  cn,
  formatDate,
  useToast,
  type ChipTone,
  type IconName,
  type LinkableDocument,
  type QuestionStage,
  type QuestionView,
} from '@dataroom/shared';
import {
  useQuestionAction,
  useRoomDocuments,
  useRoomQuestions,
  type QuestionAction,
} from '@/api/hooks';
import { PublishQuestionDialog } from '@/components/PublishQuestionDialog';
import { isUnauthorized } from '@/lib/checklist';
import { QA_STAGE_LABELS, QA_STAGES } from '@/lib/questions';
import { useRoomContext } from './context';
import { SellerQuestionsPanel } from './SellerQuestionsPanel';

const STAGE_CHIP: Record<QuestionStage, { label: string; tone: ChipTone; icon: IconName }> = {
  new: { label: 'Needs answer', tone: 'check', icon: 'help' },
  with_seller: { label: 'With seller', tone: 'processing', icon: 'users' },
  draft_ready: { label: 'Draft ready', tone: 'loi', icon: 'pen' },
  answered: { label: 'Answered', tone: 'certified', icon: 'check' },
  closed: { label: 'Closed', tone: 'na', icon: 'archive' },
};

const TOAST: Record<QuestionAction['kind'], string> = {
  answer: 'Answer sent to the buyer',
  route: 'Sent the question to the seller',
  release: 'Released the answer to the buyer',
  close: 'Closed the question',
  reopen: 'Reopened the question',
  publish: 'Saved to Buyer Questions',
  unpublish: 'Removed from Buyer Questions',
};

type Filter = 'all' | QuestionStage;

/**
 * The room's Q&A page. In a BUYER-RUN room (admin_role 'buyer') the admin is the
 * buy side, so it is "Questions to the seller" ({@link SellerQuestionsPanel});
 * every other room keeps the intermediary's buyer queue below, unchanged.
 */
export function RoomQaPage() {
  const { room } = useRoomContext();
  return room.adminRole === 'buyer' ? <SellerQuestionsPanel /> : <BuyerQaQueue />;
}

/**
 * Buyer Q&A (ADR-0039): the room's private question queue. Each buyer's
 * questions are theirs alone; the intermediary answers directly (the buyer sees
 * it at once), or sends a question to the seller and releases the seller's
 * draft — nothing a seller writes reaches a buyer without that release.
 */
function BuyerQaQueue() {
  const { roomId, room } = useRoomContext();
  const questions = useRoomQuestions(roomId);
  const documents = useRoomDocuments(roomId);
  const action = useQuestionAction(roomId);
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>('all');
  // The answered question whose Buyer Questions wording is being reviewed.
  const [publishing, setPublishing] = useState<QuestionView | null>(null);

  if (isUnauthorized(questions.error)) return <Navigate to="/login" replace />;

  const list = questions.data ?? [];
  const published = list.flatMap((q) => (q.published ? [q.published] : []));
  const byId = new Map(list.map((q) => [q.id, q]));
  const hasSeller = Boolean(room.sellerActorId);
  const publishPending = action.isPending && action.variables?.kind === 'publish';
  const library: LinkableDocument[] = (documents.data ?? []).map((d) => ({
    id: d.id,
    name: d.name,
    categoryPath: d.categoryPath,
  }));
  const countOf = (stage: QuestionStage) => list.filter((q) => q.stage === stage).length;
  const stages = QA_STAGES.filter((s) => filter === 'all' || filter === s);

  const run = (a: QuestionAction, onDone?: () => void) =>
    action.mutate(a, {
      onSuccess: () => {
        toast({ message: TOAST[a.kind] });
        onDone?.();
      },
    });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Buyer Q&amp;A</CardTitle>
          <p className="text-sm text-muted-foreground">
            Each buyer sees only their own questions, and only the answers you send or release.
          </p>
          <div className="flex flex-wrap gap-2 pt-2" role="group" aria-label="Filter by stage">
            {(['all', ...QA_STAGES] as Filter[]).map((f) => {
              const count = f === 'all' ? list.length : countOf(f);
              return (
                <Button
                  key={f}
                  type="button"
                  size="sm"
                  variant={filter === f ? 'default' : 'outline'}
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? 'All' : QA_STAGE_LABELS[f]} ({count})
                </Button>
              );
            })}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {questions.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : questions.error ? (
            <ErrorText error={questions.error} />
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No buyer questions yet. Buyers ask from the Questions tab of their data room.
            </p>
          ) : (
            stages.map((stage) => {
              const items = list.filter((q) => q.stage === stage);
              if (items.length === 0) {
                return filter === stage ? (
                  <p key={stage} className="text-sm text-muted-foreground">
                    No questions are {QA_STAGE_LABELS[stage].toLowerCase()}.
                  </p>
                ) : null;
              }
              return (
                <section key={stage} aria-label={QA_STAGE_LABELS[stage]} className="space-y-3">
                  <h3 className="text-sm font-semibold">
                    {QA_STAGE_LABELS[stage]} <span className="text-ink-3">({items.length})</span>
                  </h3>
                  <ul className="space-y-3">
                    {items.map((q) => {
                      const mine = action.variables?.questionId === q.id;
                      return (
                        <QaQuestionItem
                          key={q.id}
                          roomId={roomId}
                          question={q}
                          library={library}
                          hasSeller={hasSeller}
                          pendingKind={mine && action.isPending ? action.variables.kind : null}
                          error={mine && !action.isPending ? action.error : null}
                          errorKind={mine ? (action.variables?.kind ?? null) : null}
                          onAction={(a, onDone) => {
                            action.reset();
                            run(a, onDone);
                          }}
                          onPublish={() => {
                            action.reset();
                            setPublishing(q);
                          }}
                          onDismissError={() => action.reset()}
                        />
                      );
                    })}
                  </ul>
                </section>
              );
            })
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Buyer Questions <HelpTip id="admin.qa.buyer-questions" className="ml-1" />
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Answered questions you share with every buyer who can see the CIM — in your wording,
            never with the name of the buyer who asked. Buyers who reach the CIM later see
            everything here.
          </p>
        </CardHeader>
        <CardContent>
          <PublishedQuestionList
            questions={published}
            emptyMessage="Nothing shared yet. Use “Add to Buyer Questions” on an answered question."
            actions={(p) => {
              const q = byId.get(p.id);
              const busy = action.isPending && action.variables?.questionId === p.id;
              return q ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      action.reset();
                      setPublishing(q);
                    }}
                  >
                    Edit wording
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => run({ kind: 'unpublish', questionId: p.id })}
                  >
                    Remove from Buyer Questions
                  </Button>
                </>
              ) : null;
            }}
          />
        </CardContent>
      </Card>

      {publishing ? (
        <PublishQuestionDialog
          question={publishing}
          pending={publishPending}
          error={action.variables?.kind === 'publish' && !action.isPending ? action.error : null}
          onPublish={(wording) =>
            run({ kind: 'publish', questionId: publishing.id, wording }, () => setPublishing(null))
          }
          onClose={() => {
            action.reset();
            setPublishing(null);
          }}
        />
      ) : null}
    </div>
  );
}

interface QaQuestionItemProps {
  roomId: string;
  question: QuestionView;
  library: LinkableDocument[];
  hasSeller: boolean;
  /** The action in flight on this question, if any. */
  pendingKind: QuestionAction['kind'] | null;
  /** The last failed action on this question. */
  error: unknown;
  errorKind: QuestionAction['kind'] | null;
  onAction: (action: QuestionAction, onDone?: () => void) => void;
  /** Open the Buyer Questions wording dialog for this (answered) question. */
  onPublish: () => void;
  onDismissError: () => void;
}

function QaQuestionItem({
  roomId,
  question: q,
  library,
  hasSeller,
  pendingKind,
  error,
  errorKind,
  onAction,
  onPublish,
  onDismissError,
}: QaQuestionItemProps) {
  const [composing, setComposing] = useState(false);
  const busy = pendingKind !== null;
  const stage = q.stage ?? 'new';
  const chip = STAGE_CHIP[stage];
  const answer = q.answer;
  const isDraft = answer !== null && !answer.released;
  const canRoute = q.status === 'open' && q.routedToSeller === false && !isDraft;
  const answerError = errorKind === 'answer' ? error : null;
  // The seller's draft is only checked against the buyer here, at release: these
  // are the linked documents the asking buyer can't see.
  const hiddenFromBuyer = isDraft ? (answer.notVisibleToBuyer ?? []) : [];
  const actionError = errorKind !== 'answer' ? error : null;

  return (
    <li className="space-y-3 rounded-md border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PersonDisplay
          name={q.buyer?.displayName}
          email={q.buyer?.email}
          size="sm"
          fallback="A buyer"
        />
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone={chip.tone} icon={chip.icon}>
            {chip.label}
          </Chip>
          {q.published ? (
            <Chip tone="certified" icon="users">
              In Buyer Questions
            </Chip>
          ) : null}
          <span className="text-xs text-muted-foreground">Asked {formatDate(q.createdAt)}</span>
        </div>
      </div>

      <p className="whitespace-pre-wrap break-words text-sm">{q.body}</p>
      {q.document || q.category ? (
        <p className="text-xs text-muted-foreground">
          About: {q.document ? q.document.name || 'Untitled document' : q.category?.name}
        </p>
      ) : null}

      {answer ? (
        <div
          className={cn(
            'space-y-2 rounded-md p-3',
            isDraft ? 'border border-dashed border-line-strong' : 'bg-surface-2',
          )}
        >
          <p className="text-xs font-semibold text-ink-2">
            {isDraft
              ? "Seller's draft — not sent to the buyer yet"
              : answer.authorRole === 'seller'
                ? "Seller's answer, released to the buyer"
                : 'Your answer, sent to the buyer'}
            {answer.answeredAt ? ` · ${formatDate(answer.answeredAt)}` : ''}
          </p>
          <p className="whitespace-pre-wrap break-words text-sm">{answer.body}</p>
          <LinkedDocumentList documents={answer.documents} />
          {hiddenFromBuyer.length > 0 ? (
            <div
              className="space-y-1 rounded-md border border-line-strong bg-surface-2 p-2"
              role="note"
              aria-label="Documents this buyer can't see"
            >
              <p className="text-xs font-semibold text-destructive">
                {hiddenFromBuyer.length === 1
                  ? "1 linked document isn't visible to this buyer"
                  : `${hiddenFromBuyer.length} linked documents aren't visible to this buyer`}
              </p>
              <ul className="space-y-0.5">
                {hiddenFromBuyer.map((d) => (
                  <li key={d.id} className="truncate text-xs text-ink-2">
                    {d.name || 'Untitled document'}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">
                Release without {hiddenFromBuyer.length === 1 ? 'it' : 'them'}, edit the answer to
                remove {hiddenFromBuyer.length === 1 ? 'it' : 'them'}, or give the buyer access
                first.
              </p>
            </div>
          ) : null}
        </div>
      ) : stage === 'with_seller' ? (
        <p className="text-xs text-muted-foreground">Waiting for the seller’s draft.</p>
      ) : null}

      {composing ? (
        <AnswerComposer
          roomId={roomId}
          questionId={q.id}
          library={library}
          initial={answer ? { body: answer.body, documents: answer.documents } : null}
          buyerCannotSee={hiddenFromBuyer.map((d) => d.id)}
          label={answer ? 'Edit the answer' : 'Answer'}
          submitLabel="Send to buyer"
          hint="The buyer sees it at once."
          pending={pendingKind === 'answer'}
          error={answerError}
          onSubmit={(a) =>
            onAction({ kind: 'answer', questionId: q.id, answer: a }, () => setComposing(false))
          }
          onCancel={() => {
            onDismissError();
            setComposing(false);
          }}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {isDraft && q.status !== 'closed' ? (
            hiddenFromBuyer.length > 0 ? (
              <Button
                size="sm"
                disabled={busy}
                onClick={() =>
                  onAction({ kind: 'release', questionId: q.id, dropNotVisible: true })
                }
              >
                {pendingKind === 'release'
                  ? 'Releasing…'
                  : `Release without ${hiddenFromBuyer.length === 1 ? 'it' : 'them'}`}
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => onAction({ kind: 'release', questionId: q.id })}
              >
                {pendingKind === 'release' ? 'Releasing…' : 'Release to buyer'}
              </Button>
            )
          ) : null}
          {q.status !== 'closed' ? (
            <Button
              size="sm"
              variant={isDraft ? 'outline' : 'default'}
              disabled={busy}
              onClick={() => {
                onDismissError();
                setComposing(true);
              }}
            >
              {answer ? (isDraft ? 'Edit and send' : 'Edit answer') : 'Answer'}
            </Button>
          ) : null}
          {canRoute ? (
            hasSeller ? (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => onAction({ kind: 'route', questionId: q.id })}
              >
                {pendingKind === 'route' ? 'Sending…' : 'Send to seller'}
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                Invite the seller to send them questions.
              </span>
            )
          ) : null}
          {answer?.released ? (
            q.published ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => onAction({ kind: 'unpublish', questionId: q.id })}
              >
                {pendingKind === 'unpublish' ? 'Removing…' : 'Remove from Buyer Questions'}
              </Button>
            ) : (
              <Button size="sm" variant="outline" disabled={busy} onClick={onPublish}>
                Add to Buyer Questions
              </Button>
            )
          ) : null}
          {q.status === 'closed' ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => onAction({ kind: 'reopen', questionId: q.id })}
            >
              {pendingKind === 'reopen' ? 'Reopening…' : 'Reopen'}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => onAction({ kind: 'close', questionId: q.id })}
            >
              {pendingKind === 'close' ? 'Closing…' : 'Close'}
            </Button>
          )}
        </div>
      )}

      {!composing ? <ErrorText error={actionError ?? answerError} /> : null}
    </li>
  );
}
